import { queryOptions, useQuery } from '@tanstack/react-query';
import { env } from 'config/env';
import { http, HttpResponse, delay } from 'msw';
import { api } from 'lib/api-client';
import { QueryConfig } from 'lib/react-query';
import { ExperimentStageDataWithToolInvocations } from 'types/api';
import { createFakeExperimentStageDataWithToolInvocations } from 'mocks';

export const getExperimentDataForDatasetItem = async (
    evaluationId: string,
    datasetItemId: string,
): Promise<ExperimentStageDataWithToolInvocations[]> => {
    const response = await api.get<
        ExperimentStageDataWithToolInvocations[],
        ExperimentStageDataWithToolInvocations[]
    >(`/app/api/v1/evaluations/${evaluationId}/dataset-item-data`, {
        params: { dataset_item_id: datasetItemId },
    });
    return response;
};

export const getExperimentDataForDatasetItemQueryOptions = (
    evaluationId: string,
    datasetItemId: string,
) => {
    return queryOptions({
        queryKey: [
            'experiment-data-for-dataset-item',
            evaluationId,
            datasetItemId,
        ],
        queryFn: () =>
            getExperimentDataForDatasetItem(evaluationId, datasetItemId),
    });
};

type UseExperimentDataForDatasetItemOptions = {
    evaluationId: string;
    datasetItemId: string;
    queryConfig?: QueryConfig<
        typeof getExperimentDataForDatasetItemQueryOptions
    >;
};

export const useExperimentDataForDatasetItem = ({
    evaluationId,
    datasetItemId,
    queryConfig,
}: UseExperimentDataForDatasetItemOptions) => {
    return useQuery({
        ...getExperimentDataForDatasetItemQueryOptions(
            evaluationId,
            datasetItemId,
        ),
        ...queryConfig,
    });
};

export const getExperimentDataForDatasetItemMSWHandler = () =>
    http.get(
        `${env.API_URL}/app/api/v1/evaluations/:evaluationId/dataset-item-data`,
        async () => {
            await delay(1000);

            // Create 3-5 stages with realistic progression
            const numStages = 4;
            const stages = Array.from({ length: numStages }, (_, index) =>
                createFakeExperimentStageDataWithToolInvocations({
                    experimentStageId: `stage-${index}`,
                    numToolInvocations: index === 1 ? 2 : 1, // Middle stage has more invocations
                }),
            );

            return HttpResponse.json(stages);
        },
    );
